/**
 * Generic anchored mini-modal container for parameter selectors.
 * Handles positioning near a trigger element, viewport clamping,
 * close interactions, dark mode styling, and optional title rendering.
 */

import React, {
    type CSSProperties,
    type ReactNode,
    type RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { MiniModalScopeContext, type MiniModalScopeContextValue } from "./MiniModalScopeContext";
import MiniModalTitle from "./MiniModalTitle";
import { isMiniModalAnchoredOverlayNode } from "./miniModal.overlay.configuration";

export type MiniModalPlacement = "left" | "right" | "top" | "bottom";

interface MiniModalPosition {
    "top":  number;
    "left": number;
}

interface MiniModalLayoutState {
    "position":  MiniModalPosition | null;
    "placement": MiniModalPlacement;
    "tether":    MiniModalTether | null;
}

type MiniModalAnimationPhase = "hidden" | "entering" | "entered" | "exiting";

const MINI_MODAL_EXIT_ANIMATION_MS = 280;

interface MiniModalTether {
    "startX": number;
    "startY": number;
    "endX":   number;
    "endY":   number;
}

interface MiniModalProps {

    /** Controls whether the mini-modal is rendered. */
    "isOpen": boolean;

    /** Called when the mini-modal requests to close. */
    "onClose": () => void;

    /**
     * Reference to the trigger element used as anchor for positioning,
     * outside-click detection and visual tether origin.
     * Accepts a nullable ref so callers can pass either their own local
     * `useRef<HTMLButtonElement | null>( null )` or a ref received from
     * a context (e.g. `FloatingActionBurst`'s main FAB ref) without
     * extra cast.
     */
    "anchorRef": RefObject<HTMLElement | null>;

    /** Enables dark palette variants for the mini-modal UI. */
    "isDarkUi": boolean;

    /**
     * Optional panel title. Rendered as a heading, not a {@link SectionLabel},
     * so it stays distinct from in-content section separators.
     */
    "title"?: string;

    /** Optional preferred placement relative to the trigger. */
    "placement"?: MiniModalPlacement;

    /** Gap in pixels between trigger and mini-modal. */
    "offset"?: number;

    /** Minimum spacing in pixels from viewport edges. */
    "viewportMargin"?: number;

    /** Optional max width in pixels or CSS units. */
    "maxWidth"?: number | string;

    /** Optional extra class names for the container. */
    "className"?: string;

    /**
     * When `true`, drop the body top padding (`p-3` → `px-3 pb-3 pt-0`).
     * Useful when the first child is tab chrome that should sit flush
     * under the title.
     */
    "disableContentPaddingTop"?: boolean;

    /** Mini-modal content. */
    "children": ReactNode;
}

/**
 * Resolves width values from numeric or string props.
 * @param value Width prop value.
 * @returns CSS width value.
 */
const resolveCssSize = ( value: number | string ): string => {
    if ( typeof value === "number" ) {
        return `${ value }px`;
    }

    return value;
};

/**
 * Returns animation styles for an original "kinetic unfold" entrance:
 * the panel appears from the trigger side with blur, tilt, and clipping.
 * @param placement Effective panel placement after viewport flip logic.
 * @param animationPhase Current animation lifecycle phase.
 * @param hasMeasuredPosition Whether the panel has a measured final position.
 * @returns Inline animation style values.
 */
const getPlacementAnimationStyle = (
    placement: MiniModalPlacement,
    animationPhase: MiniModalAnimationPhase,
    hasMeasuredPosition: boolean,
): CSSProperties => {
    let transformOrigin = "center top";
    if ( placement === "left" ) {
        transformOrigin = "right center";
    } else if ( placement === "right" ) {
        transformOrigin = "left center";
    } else if ( placement === "top" ) {
        transformOrigin = "center bottom";
    }

    const baseStyle: CSSProperties = {
        "transformOrigin": transformOrigin,
        "transition":      "transform 280ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 220ms ease-out, filter 260ms ease-out, clip-path 280ms cubic-bezier(0.2, 0.8, 0.2, 1)",
        "willChange":      "transform, opacity, filter, clip-path",
    };

    if ( hasMeasuredPosition === false ) {
        return {
            ...baseStyle,
            "opacity":   0,
            "transform": "translate3d(0, 0, 0) scale(1) rotate(0deg)",
            "filter":    "blur(0px) saturate(1)",
            "clipPath":  "inset(0% 0% 0% 0% round 16px)",
        };
    }

    if ( animationPhase === "entered" ) {
        return {
            ...baseStyle,
            "opacity":   1,
            "transform": "translate3d(0, 0, 0) scale(1) rotate(0deg)",
            "filter":    "blur(0px) saturate(1)",
            "clipPath":  "inset(0% 0% 0% 0% round 16px)",
        };
    }

    if ( placement === "left" ) {
        return {
            ...baseStyle,
            "opacity":   0,
            "transform": "translate3d(14px, 0, 0) scale(0.94) rotate(-1.6deg)",
            "filter":    "blur(4px) saturate(0.94)",
            "clipPath":  "inset(8% 7% 8% 7% round 16px)",
        };
    }

    if ( placement === "right" ) {
        return {
            ...baseStyle,
            "opacity":   0,
            "transform": "translate3d(-14px, 0, 0) scale(0.94) rotate(1.6deg)",
            "filter":    "blur(4px) saturate(0.94)",
            "clipPath":  "inset(8% 7% 8% 7% round 16px)",
        };
    }

    if ( placement === "top" ) {
        return {
            ...baseStyle,
            "opacity":   0,
            "transform": "translate3d(0, 14px, 0) scale(0.94) rotate(-0.9deg)",
            "filter":    "blur(4px) saturate(0.94)",
            "clipPath":  "inset(9% 6% 7% 6% round 16px)",
        };
    }

    return {
        ...baseStyle,
        "opacity":   0,
        "transform": "translate3d(0, -14px, 0) scale(0.94) rotate(0.9deg)",
        "filter":    "blur(4px) saturate(0.94)",
        "clipPath":  "inset(7% 6% 9% 6% round 16px)",
    };
};

/**
 * Computes style for the visual link between trigger and modal.
 * @param tether Coordinates for the connector segment.
 * @returns Inline style object for tether rendering.
 */
const getTetherStyle = ( tether: MiniModalTether ): CSSProperties => {
    const deltaX = tether.endX - tether.startX;
    const deltaY = tether.endY - tether.startY;
    const length = Math.hypot( deltaX, deltaY );
    const angle = Math.atan2( deltaY, deltaX ) * ( 180 / Math.PI );

    return {
        "position":        "fixed",
        "top":             tether.startY,
        "left":            tether.startX,
        "width":           length,
        "height":          4,
        "transform":       `rotate(${ angle }deg)`,
        "transformOrigin": "0 50%",
        "borderRadius":    9999,
        "background":      "linear-gradient(90deg, rgba(17, 211, 156, 0.62) 0%, rgba(17, 211, 156, 0.5) 55%, rgba(17, 211, 156, 0.28) 100%)",
        "boxShadow":       "0 0 10px rgba(17, 211, 156, 0.24)",
        "pointerEvents":   "none",
        "filter":          "drop-shadow(0 0 4px rgba(17, 211, 156, 0.28))",
    };
};

/**
 * Generic floating mini-modal rendered near a trigger element.
 * @param props Mini-modal configuration and content.
 * @returns Anchored floating modal element.
 */
const MiniModal: React.FC<MiniModalProps> = ( {
    isOpen,
    onClose,
    anchorRef,
    isDarkUi,
    title,
    placement = "left",
    offset = 18,
    viewportMargin = 8,
    maxWidth = 320,
    className = "",
    disableContentPaddingTop = false,
    children,
} ) => {
    const panelRef = useRef<HTMLDivElement | null>( null );
    const openEnterFrameRef = useRef<number | null>( null );
    const openComputeFrameRef = useRef<number | null>( null );
    const closeTimeoutRef = useRef<number | null>( null );
    const animationPhaseRef = useRef<MiniModalAnimationPhase>( "hidden" );
    const [ layoutState, setLayoutState ] = useState<MiniModalLayoutState>( {
        "position":  null,
        "placement": placement,
        "tether":    null,
    } );
    const [ animationPhase, setAnimationPhase ] = useState<MiniModalAnimationPhase>( "hidden" );
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const miniModalScope = useMemo<MiniModalScopeContextValue>( () => ( { "requestClose": onClose } ), [ onClose ] );

    /**
     * Ignores scroll events that originate inside the panel or a portaled
     * mini-modal overlay so nested scroll areas do not re-anchor the panel.
     *
     * @param event Captured viewport event.
     * @returns `true` when repositioning should be skipped.
     */
    const shouldIgnoreViewportUpdate = useCallback( ( event: Event ): boolean => {
        if ( event.type !== "scroll" ) {
            return false;
        }

        const target = event.target;

        if ( !( target instanceof Node ) ) {
            return false;
        }

        if ( panelRef.current?.contains( target ) === true ) {
            return true;
        }

        return isMiniModalAnchoredOverlayNode( target );
    }, [] );

    useEffect( () => {
        animationPhaseRef.current = animationPhase;
    }, [ animationPhase ] );

    /**
     * Computes the panel position from the trigger geometry.
     * The logic first tries the preferred placement and then flips to
     * the opposite side when there is not enough space.
     */
    const computePosition = useCallback( (): boolean => {
        const panelElement = panelRef.current;
        const anchorElement = anchorRef.current;

        if ( panelElement === null || anchorElement === null ) {
            return false;
        }

        const anchorRect = anchorElement.getBoundingClientRect();
        const panelRect = panelElement.getBoundingClientRect();
        const maxLeft = Math.max( viewportMargin, window.innerWidth - panelRect.width - viewportMargin );
        const maxTop = Math.max( viewportMargin, window.innerHeight - panelRect.height - viewportMargin );

        const availableLeft = anchorRect.left - viewportMargin;
        const availableRight = window.innerWidth - anchorRect.right - viewportMargin;
        const availableTop = anchorRect.top - viewportMargin;
        const availableBottom = window.innerHeight - anchorRect.bottom - viewportMargin;

        // Flip placement when the preferred side cannot host the panel.
        let effectivePlacement: MiniModalPlacement = placement;
        if ( placement === "left" && availableLeft < panelRect.width + offset && availableRight >= panelRect.width + offset ) {
            effectivePlacement = "right";
        } else if ( placement === "right" && availableRight < panelRect.width + offset && availableLeft >= panelRect.width + offset ) {
            effectivePlacement = "left";
        } else if ( placement === "top" && availableTop < panelRect.height + offset && availableBottom >= panelRect.height + offset ) {
            effectivePlacement = "bottom";
        } else if ( placement === "bottom" && availableBottom < panelRect.height + offset && availableTop >= panelRect.height + offset ) {
            effectivePlacement = "top";
        }

        let left = anchorRect.left;
        let top = anchorRect.top;

        if ( effectivePlacement === "left" ) {
            left = anchorRect.left - panelRect.width - offset;
            top = anchorRect.top + ( anchorRect.height / 2 ) - ( panelRect.height / 2 );
        } else if ( effectivePlacement === "right" ) {
            left = anchorRect.right + offset;
            top = anchorRect.top + ( anchorRect.height / 2 ) - ( panelRect.height / 2 );
        } else if ( effectivePlacement === "top" ) {
            left = anchorRect.left + ( anchorRect.width / 2 ) - ( panelRect.width / 2 );
            top = anchorRect.top - panelRect.height - offset;
        } else {
            left = anchorRect.left + ( anchorRect.width / 2 ) - ( panelRect.width / 2 );
            top = anchorRect.bottom + offset;
        }

        const clampedLeft = Math.max( viewportMargin, Math.min( left, maxLeft ) );
        const clampedTop = Math.max( viewportMargin, Math.min( top, maxTop ) );

        let tetherStartX = anchorRect.left + ( anchorRect.width / 2 );
        let tetherStartY = anchorRect.top + ( anchorRect.height / 2 );
        let tetherEndX = clampedLeft + ( panelRect.width / 2 );
        let tetherEndY = clampedTop + ( panelRect.height / 2 );

        if ( effectivePlacement === "left" ) {
            tetherStartX = anchorRect.left;
            tetherStartY = anchorRect.top + ( anchorRect.height / 2 );
            tetherEndX = clampedLeft + panelRect.width;
            tetherEndY = clampedTop + ( panelRect.height / 2 );
        } else if ( effectivePlacement === "right" ) {
            tetherStartX = anchorRect.right;
            tetherStartY = anchorRect.top + ( anchorRect.height / 2 );
            tetherEndX = clampedLeft;
            tetherEndY = clampedTop + ( panelRect.height / 2 );
        } else if ( effectivePlacement === "top" ) {
            tetherStartX = anchorRect.left + ( anchorRect.width / 2 );
            tetherStartY = anchorRect.top;
            tetherEndX = clampedLeft + ( panelRect.width / 2 );
            tetherEndY = clampedTop + panelRect.height;
        } else {
            tetherStartX = anchorRect.left + ( anchorRect.width / 2 );
            tetherStartY = anchorRect.bottom;
            tetherEndX = clampedLeft + ( panelRect.width / 2 );
            tetherEndY = clampedTop;
        }

        // Final viewport-safe position, including top overflow protection.
        setLayoutState( {
            "position": {
                "top":  clampedTop,
                "left": clampedLeft,
            },
            "placement": effectivePlacement,
            "tether":    {
                "startX": tetherStartX,
                "startY": tetherStartY,
                "endX":   tetherEndX,
                "endY":   tetherEndY,
            },
        } );

        return true;
    }, [ anchorRef, offset, placement, viewportMargin ] );

    useEffect( () => {
        if ( isOpen ) {
            if ( closeTimeoutRef.current !== null ) {
                window.clearTimeout( closeTimeoutRef.current );
                closeTimeoutRef.current = null;
            }

            // Re-arm the whole entrance sequence every time the modal opens.
            openEnterFrameRef.current = window.requestAnimationFrame( () => {
                setAnimationPhase( "entering" );
                setLayoutState( {
                    "position":  null,
                    "placement": placement,
                    "tether":    null,
                } );

                // Wait for mount, then measure. Retry briefly if refs are not ready yet.
                const runPositioning = ( attemptsLeft: number ): void => {
                    openComputeFrameRef.current = window.requestAnimationFrame( () => {
                        const didPosition = computePosition();
                        if ( didPosition ) {
                            setAnimationPhase( "entered" );

                            return;
                        }
                        if ( attemptsLeft > 0 ) {
                            runPositioning( attemptsLeft - 1 );
                        }
                    } );
                };

                runPositioning( 5 );
            } );
        } else if ( animationPhaseRef.current !== "hidden" && animationPhaseRef.current !== "exiting" ) {
            // Keep the panel mounted for exit animation before unmounting.
            openEnterFrameRef.current = window.requestAnimationFrame( () => {
                setAnimationPhase( "exiting" );
            } );
            closeTimeoutRef.current = window.setTimeout( () => {
                setAnimationPhase( "hidden" );
            }, MINI_MODAL_EXIT_ANIMATION_MS );
        }

        const handleViewportUpdate = ( event: Event ): void => {
            if ( shouldIgnoreViewportUpdate( event ) ) {
                return;
            }

            computePosition();
        };

        window.addEventListener( "resize", handleViewportUpdate );
        window.addEventListener( "scroll", handleViewportUpdate, true );

        return () => {
            if ( openComputeFrameRef.current !== null ) {
                window.cancelAnimationFrame( openComputeFrameRef.current );
                openComputeFrameRef.current = null;
            }
            if ( openEnterFrameRef.current !== null ) {
                window.cancelAnimationFrame( openEnterFrameRef.current );
                openEnterFrameRef.current = null;
            }
            window.removeEventListener( "resize", handleViewportUpdate );
            window.removeEventListener( "scroll", handleViewportUpdate, true );
        };
    }, [ computePosition, isOpen, placement, shouldIgnoreViewportUpdate ] );

    useEffect( () => {
        return () => {
            if ( openComputeFrameRef.current !== null ) {
                window.cancelAnimationFrame( openComputeFrameRef.current );
            }
            if ( openEnterFrameRef.current !== null ) {
                window.cancelAnimationFrame( openEnterFrameRef.current );
            }
            if ( closeTimeoutRef.current !== null ) {
                window.clearTimeout( closeTimeoutRef.current );
            }
        };
    }, [] );

    useEffect( () => {
        if ( !isOpen ) {
            return;
        }

        // Close when interactions happen outside both the trigger and panel.
        const handlePointerDown = ( event: PointerEvent ): void => {
            const panelElement = panelRef.current;
            const anchorElement = anchorRef.current;
            const targetNode = event.target as Node;

            if ( isMiniModalAnchoredOverlayNode( targetNode ) ) {
                return;
            }

            if (
                panelElement !== null &&
                !panelElement.contains( targetNode ) &&
                anchorElement !== null &&
                !anchorElement.contains( targetNode )
            ) {
                onClose();
            }
        };

        // Keyboard escape provides a predictable dismissal behavior.
        const handleEscape = ( event: KeyboardEvent ): void => {
            if ( event.key === "Escape" ) {
                onClose();
            }
        };

        document.addEventListener( "pointerdown", handlePointerDown );
        document.addEventListener( "keydown", handleEscape );

        return () => {
            document.removeEventListener( "pointerdown", handlePointerDown );
            document.removeEventListener( "keydown", handleEscape );
        };
    }, [ anchorRef, isOpen, onClose ] );

    if ( animationPhase === "hidden" ) {
        return null;
    }

    const hasMeasuredPosition = layoutState.position !== null;
    const panelStyle: CSSProperties = {
        "position":        "fixed",
        "top":             layoutState.position?.top ?? viewportMargin,
        "left":            layoutState.position?.left ?? viewportMargin,
        "maxWidth":        resolveCssSize( maxWidth ),
        "maxHeight":       `calc(100vh - ${ viewportMargin * 2 }px)`,
        "width":           "min(92vw, 320px)",
        "zIndex":          2147483647,
        "backgroundColor": palette.overlayCardBackgroundStrong,
        "borderColor":     palette.overlayBorder,
        "color":           palette.overlayTextPrimary,
        "pointerEvents":   animationPhase === "entered" && hasMeasuredPosition ? "auto" : "none",
    };
    const animationStyle = getPlacementAnimationStyle( layoutState.placement, animationPhase, hasMeasuredPosition );
    const tetherStyle = layoutState.tether !== null ? getTetherStyle( layoutState.tether ) : null;
    const tetherOpacity = animationPhase === "exiting" ? 0 : animationPhase === "entered" ? 0.75 : 0.55;

    return createPortal(
        <div
            className="fixed inset-0 pointer-events-none z-[2147483646]"
            data-mini-modal="true"
        >

            {hasMeasuredPosition && tetherStyle !== null && (
                <div
                    style={{
                        ...tetherStyle,
                        "opacity":    tetherOpacity,
                        "transition": "opacity 160ms ease-out",
                    }}
                />
            )}
            <div
                className={
                    `pointer-events-auto rounded-xl border shadow-[0_16px_36px_rgba(15,23,42,0.48)] backdrop-blur-sm flex flex-col overflow-hidden ${ className }`
                }
                ref={panelRef}
                style={{
                    ...panelStyle,
                    ...animationStyle,
                }}
            >
                {title !== undefined && title.length > 0 && (
                    <MiniModalTitle
                        isDarkUi={isDarkUi}
                        isEntered={animationPhase === "entered" && hasMeasuredPosition}
                        title={title}
                    />
                )}
                <div
                    className={
                        disableContentPaddingTop
                            ? "min-h-0 flex-1 overflow-y-auto px-3 pb-3 pt-0"
                            : "min-h-0 flex-1 overflow-y-auto p-3"
                    }
                >
                    <MiniModalScopeContext.Provider value={miniModalScope}>
                        {children}
                    </MiniModalScopeContext.Provider>
                </div>

            </div>
        </div>,
        document.body,
    );
};

export default MiniModal;
